import type { Fetcher } from 'swr';
import useSWR from 'swr';

import { carsHistoryList } from '~/data/generated/endpoints/cars/cars';
import type { CarHistoryEvent } from '~/data/generated/model';
import { CarsHistoryListResponse } from '~/data/generated/zod/cars/cars.zod';
import { parseApiResponse } from '~/data/parseApiResponse';

type CarHistoryKey = readonly ['car-history', carId: string, types: string];

// `types` is a pre-joined, sorted comma list so the SWR cache key is stable for
// the same selection regardless of chip order. Empty means "all types" and the
// `type` query param is omitted.
const historyFetcher: Fetcher<CarHistoryEvent[], CarHistoryKey> = async ([, carId, types]) => {
  const { data } = await carsHistoryList(carId, types ? { type: types } : undefined);
  return parseApiResponse(CarsHistoryListResponse, data, 'CarHistory');
};

export default function useCarHistory(carId: string | undefined, types?: readonly string[]) {
  const typeParam = types && types.length ? [...types].sort().join(',') : '';

  const {
    data,
    error,
    isLoading: loading,
  } = useSWR(carId ? (['car-history', carId, typeParam] as const) : null, historyFetcher);

  return { events: data ?? [], error, loading };
}
